"use client"

import { useForm, usePage } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { InputBase, TextField } from "@/components/ui/input"
import { Label } from "@/components/ui/input"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"
import SettingsLayout from "@/layouts/settings/layout"
import { toFieldError } from "@/lib/utils"
import { settingsPasswordPath } from "@/routes"

export default function Password() {
  const { auth } = usePage().props
  const { data, setData, put, processing, errors, reset } = useForm({
    current_password: "",
    password: "",
    password_confirmation: "",
  })

  if (auth?.user?.oauth_user) {
    return (
      <SettingsLayout>
        <div className="flex flex-col gap-6 px-4 lg:px-8">
          <SectionHeader.Root>
            <SectionHeader.Group>
              <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
                <SectionHeader.Heading>Password</SectionHeader.Heading>
                <SectionHeader.Subheading>
                  Your password is managed through Salesforce. To change your
                  password, please contact your Salesforce administrator.
                </SectionHeader.Subheading>
              </div>
            </SectionHeader.Group>
          </SectionHeader.Root>
        </div>
      </SettingsLayout>
    )
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    put(settingsPasswordPath(), {
      onSuccess: () => reset(),
    })
  }

  return (
    <SettingsLayout>
      <form className="flex flex-col gap-6 px-4 lg:px-8" onSubmit={submit}>
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>Password</SectionHeader.Heading>
              <SectionHeader.Subheading>
                Please enter your current password to change your password.
              </SectionHeader.Subheading>
            </div>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              isRequired
              size="sm"
              title="Current password"
              className="max-lg:hidden"
            />

            <TextField
              isRequired
              name="current_password"
              value={data.current_password}
              onChange={(v) => setData("current_password", v)}
              isInvalid={!!errors.current_password}
              error={toFieldError(errors.current_password)}
            >
              <Label className="lg:hidden">Current password</Label>
              <InputBase type="password" size="md" />
            </TextField>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              isRequired
              size="sm"
              title="New password"
              className="max-lg:hidden"
            />

            <TextField
              isRequired
              name="password"
              value={data.password}
              onChange={(v) => setData("password", v)}
              isInvalid={!!errors.password}
              error={toFieldError(errors.password)}
            >
              <Label className="lg:hidden">New password</Label>
              <InputBase type="password" size="md" />
            </TextField>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              isRequired
              size="sm"
              title="Confirm password"
              className="max-lg:hidden"
            />

            <TextField
              isRequired
              name="password_confirmation"
              value={data.password_confirmation}
              onChange={(v) => setData("password_confirmation", v)}
              isInvalid={!!errors.password_confirmation}
              error={toFieldError(errors.password_confirmation)}
            >
              <Label className="lg:hidden">Confirm password</Label>
              <InputBase type="password" size="md" />
            </TextField>
          </div>
        </div>

        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button
              type="submit"
              color="primary"
              size="md"
              isDisabled={processing}
            >
              Update password
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </form>
    </SettingsLayout>
  )
}
